/**
 * What the address decides: which firm's brand the signed-out pages wear, and
 * which address a firm's client and contractor links should use (Phase 27).
 *
 * ─── THE RULE ───────────────────────────────────────────────────────────────
 *   app. / www. / the bare product domain   → neutral Design Studio HQ
 *   <slug>.designstudiohq.com               → that firm's brand, looked up by
 *                                             slug (public_firm_brand, 0037)
 *   a legacy address (LEGACY_HOSTS)          → the firm it belongs to
 *   anything else (localhost, workers.dev)   → neutral
 *
 * No firm's name is ever built in. Until 0037, "Tonie Searle Designs" was the
 * fallback for every unrecognized address — and would have greeted another
 * firm's people. Tonie's address still shows her brand, but because her
 * firm's data says so, not because the code does.
 *
 * Showing a firm's brand on an address that isn't theirs is the one outcome
 * this must never produce; an unknown slug gets "no firm at this address".
 */

export const PRODUCT_NAME = 'Design Studio HQ';
/** Base domain (F5: configuration, not scattered literals). */
export const PRODUCT_DOMAIN = 'designstudiohq.com';

/** Product subdomains that belong to no firm. */
const NEUTRAL_SUBDOMAINS = new Set(['app', 'www']);

/**
 * Addresses outside the product domain that belong to a firm (F9). Tonie's
 * clients hold links on this host, so it must keep working — and keep being
 * the host her new links use.
 */
export const LEGACY_HOSTS: Readonly<Record<string, string>> = {
  'toniesearledesigns.digitaltoolcenter.com': 'toniesearledesigns',
};

/**
 * Whether `<slug>.designstudiohq.com` actually reaches the app yet. Needs the
 * wildcard DNS record and Worker route (MULTI_FIRM_PLAN.md, Phase 27). Until
 * then a firm's links must stay on the address they were made from — a link
 * to an address that doesn't resolve is a broken link in a client's inbox.
 */
export const VANITY_SUBDOMAINS_LIVE = false;

function norm(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, '');
}

/**
 * True for the product domain and its subdomains. Matched on a dot boundary,
 * never a bare endsWith: that would also match `evil-designstudiohq.com`.
 */
export function isProductHost(hostname: string): boolean {
  const h = norm(hostname);
  return h === PRODUCT_DOMAIN || h.endsWith(`.${PRODUCT_DOMAIN}`);
}

/** The firm slug an address belongs to, or null for a neutral address. */
export function hostSlug(hostname: string): string | null {
  const h = norm(hostname);
  if (LEGACY_HOSTS[h]) return LEGACY_HOSTS[h]!;
  if (!h.endsWith(`.${PRODUCT_DOMAIN}`)) return null;
  const sub = h.slice(0, -(PRODUCT_DOMAIN.length + 1));
  // One level only: a.b.designstudiohq.com is nobody's.
  if (!sub || sub.includes('.') || NEUTRAL_SUBDOMAINS.has(sub)) return null;
  return sub;
}

/** The public address for a firm's links, or null to stay where we are. */
export function firmHost(slug: string | null | undefined, vanityLive = VANITY_SUBDOMAINS_LIVE): string | null {
  if (!slug) return null;
  const legacy = Object.entries(LEGACY_HOSTS).find(([, s]) => s === slug);
  if (legacy) return legacy[0];
  return vanityLive ? `${slug}.${PRODUCT_DOMAIN}` : null;
}

/**
 * The origin a firm's client and contractor links should use: the firm's own
 * address when it has a working one, otherwise wherever the designer is.
 */
export function publicOrigin(
  slug: string | null | undefined, currentOrigin: string, vanityLive = VANITY_SUBDOMAINS_LIVE,
): string {
  const host = firmHost(slug, vanityLive);
  return host ? `https://${host}` : currentOrigin;
}

// ─── The brand signed-out pages wear ────────────────────────────────────────

export interface SignInBrand {
  name: string;
  /** Two letters for the circular mark. */
  monogram: string;
  /** Suffix for the browser tab title. */
  titleBase: string;
  /** 0045 — the firm's chosen typeface and color, so their own sign-in page
   *  looks like their brand and not like the product's. */
  fontKey?: string | null;
  color?: string | null;
}

/** What public_firm_brand() (0037) returns for a slug. */
export interface PublicFirmBrand {
  firm_name?: string | null;
  company_name?: string | null;
  tagline?: string | null;
  logo_storage_path?: string | null;
  /** 0045 — the type the firm's name is set in, on their own address too. */
  brand_font?: string | null;
  brand_color?: string | null;
}

export const NEUTRAL_BRAND: SignInBrand = { name: PRODUCT_NAME, monogram: 'DS', titleBase: PRODUCT_NAME };

/**
 * Initials for the circular mark: first letters of the first two words.
 *
 * "Letter" means any script's, not just ASCII. Stripping to `[A-Za-z0-9]`
 * turned "Åse & Co" into "SC" and would have drawn "Ñuñez Design" as "UD" —
 * the accented letter dropped and the NEXT one promoted, which reads as a
 * typo rather than as a mark (2026-09-30). Punctuation is still skipped, so
 * "& Co. Interiors" is "CI".
 */
export function monogramFor(name: string): string {
  const letters = /[\p{L}\p{N}]/u;
  const words = name.split(/\s+/).filter((w) => letters.test(w));
  if (words.length === 0) return 'DS';
  const strip = (w: string): string => w.replace(/[^\p{L}\p{N}]/gu, '');
  if (words.length === 1) return strip(words[0]!).slice(0, 2).toUpperCase();
  return (strip(words[0]!).charAt(0) + strip(words[1]!).charAt(0)).toUpperCase();
}


/** A firm's display name: its saved company name, else its own name. */
export function firmDisplayName(b: PublicFirmBrand | null | undefined): string | null {
  return b?.company_name?.trim() || b?.firm_name?.trim() || null;
}

export function brandFor(b: PublicFirmBrand | null | undefined): SignInBrand {
  const name = firmDisplayName(b);
  if (!name) return NEUTRAL_BRAND;
  return {
    name, monogram: monogramFor(name), titleBase: name,
    fontKey: b?.brand_font ?? null, color: b?.brand_color ?? null,
  };
}

// The address's firm, looked up once at boot (main.ts) — signed-out pages
// render synchronously and read it from here.
let hostBrand: { slug: string; brand: PublicFirmBrand | null } | null = null;

/** Record what the lookup found for this address's slug (null = no such firm). */
export function setHostFirmBrand(slug: string, brand: PublicFirmBrand | null): void {
  hostBrand = { slug, brand };
}

/** For tests. */
export function clearHostFirmBrand(): void {
  hostBrand = null;
}

/** 'found' / 'missing' once looked up; 'unknown' if not (yet, or neutral). */
export function hostFirmState(hostname: string): 'neutral' | 'found' | 'missing' | 'unknown' {
  const slug = hostSlug(hostname);
  if (!slug) return 'neutral';
  if (!hostBrand || hostBrand.slug !== slug) return 'unknown';
  return hostBrand.brand ? 'found' : 'missing';
}

/**
 * Whether to refuse this address as "no firm here". Only a product subdomain
 * can be refused. A legacy address is one WE configured (LEGACY_HOSTS), so a
 * failed match means our mapping or the firm's slug is wrong — the people on
 * it must still be able to sign in.
 *
 * 2026-09-19: Tonie's firm had no slug in production (0030 derived slugs only
 * from saved branding, which she had never saved), so her own address showed
 * "No firm at this address" until the slug was set by hand.
 */
export function refuseAsUnknownFirm(hostname: string): boolean {
  return hostFirmState(hostname) === 'missing' && !LEGACY_HOSTS[norm(hostname)];
}

/** The looked-up brand for this address, when there is one. */
export function hostFirmBrand(hostname: string): PublicFirmBrand | null {
  return hostFirmState(hostname) === 'found' ? hostBrand!.brand : null;
}

/**
 * The brand for a signed-out page on this address. Neutral unless the
 * address's own firm has been looked up and found — never a guess.
 */
export function signInBrand(hostname: string): SignInBrand {
  return brandFor(hostFirmBrand(hostname));
}

/**
 * Signed in on an address that belongs to a DIFFERENT firm (F2/F4: the app
 * follows your membership, not the URL). Returns a sentence to show, or null.
 */
export function hostMismatchNotice(
  hostname: string, myFirmSlug: string | null | undefined, myFirmName: string,
): string | null {
  const slug = hostSlug(hostname);
  if (!slug || slug === myFirmSlug) return null;
  const theirs = firmDisplayName(hostFirmBrand(hostname)) ?? 'another firm';
  return `You’re signed in to ${myFirmName} on ${theirs}’s address. Your firm’s own sign-in is app.${PRODUCT_DOMAIN}.`;
}
